/*
===============================================================================================================
                                 Copyright 2022-2025, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

/* eslint-disable no-console */
import { isEmpty } from '@/utilities/validation';
import { isBoolean, includes } from 'lodash';
import { updateCompanyRequest } from './company';
import compileChangeObject from './compile-change-object';
import { errorHandler } from '../error-handling';

export default async ({
  changes: changeArray, merchantId, token,
  rootOrgId, productName, getConfigValue,
  getFeature, sharedData,
}) => {
  const { carrierCompanyId } = sharedData || {};
  const { multiCarrierEnabled, carriersWithPendingStatus } = getFeature('carriers') || {};
  const carrierNetworksEnabled = getFeature('carriers').carrierNetworksEnabled || [];
  let earlyReturn;

  if (isEmpty(changeArray) && (!multiCarrierEnabled && includes(carrierNetworksEnabled, carrierCompanyId))) {
    earlyReturn = false;
  } else if (isEmpty(changeArray)) {
    earlyReturn = true;
  }

  if (earlyReturn) return;
  const changes = compileChangeObject(changeArray);
  const currentCarriers = getConfigValue('carriersConfig') || [];

  if (isEmpty(currentCarriers) && isEmpty(changes) && !multiCarrierEnabled) {
    return;
  }
  const existingCarrier = currentCarriers.find((el) => el.carrierCompanyId === carrierCompanyId);

  const carrierShouldBePending = carriersWithPendingStatus && carriersWithPendingStatus.includes(carrierCompanyId);
  const hasDefaultCarrier = currentCarriers.some((el) => el.default);

  let updatedCarrier;

  const defaultCarrier = {
    carrierCompanyId,
    ...changes,
  };

  if (!existingCarrier) {
    updatedCarrier = {
      enabled: !carrierShouldBePending,
      default: !!(!carrierShouldBePending && !hasDefaultCarrier),
      ...defaultCarrier,
    };
  } else {
    updatedCarrier = {
      ...existingCarrier,
      ...changes,
    };
  }

  let updatedList = currentCarriers
    .filter((el) => el.carrierCompanyId !== carrierCompanyId);

  if (!multiCarrierEnabled) {
    // for non-multi carrier - toggle rest of the carriers off, when updating a carrier
    updatedList = [];
    if (!carrierShouldBePending) {
      updatedCarrier.default = true;
    }
  }

  if (includes(carrierNetworksEnabled, carrierCompanyId)) {
    updatedCarrier = [];
    if (multiCarrierEnabled) {
      updatedList = currentCarriers
        .filter((el) => el.carrierCompanyId !== carrierCompanyId);
    }
  }

  if (changes.default) {
    updatedList = updatedList.map((el) => ({ ...el, default: false }));
    updatedCarrier.default = true;
  }

  const body = {
    products: {
      [productName]: {
        carrierConfig: {
          carriers: []
            .concat(updatedList)
            .concat(isBoolean(updatedCarrier.deleteCarrier) && updatedCarrier.deleteCarrier === true ? [] : updatedCarrier),
        },
        enabled: true,
      },
    },
  };

  try {
    await updateCompanyRequest({
      rootOrgId, merchantId, body, token,
    });
  } catch (e) {
    console.log('Could not update company carrier config');
    console.log(e);
    throw new Error(errorHandler.checkInvalidCarrierError(e));
  }
};

export const enableCarrierInSingleCarrierMode = async ({
  merchantId, getConfigValue, carrierCompanyId,
  rootOrgId, token, productName,
}) => {
  const merchantCarriers = getConfigValue('carriersConfig') || [];

  const updatedList = merchantCarriers
    .map((el) => {
      if (el.carrierCompanyId === carrierCompanyId) {
        return { ...el, enabled: true, default: true };
      }
      return el;
    });

  const body = {
    products: {
      [productName]: {
        carrierConfig: {
          carriers: updatedList,
        },
        enabled: true,
      },
    },
  };

  try {
    await updateCompanyRequest({
      rootOrgId, merchantId, body, token,
    });
  } catch (e) {
    console.log('Could not enable carrier');
    console.log(e);
    throw new Error(errorHandler.checkInvalidCarrierError(e));
  }
};
